'use client'
import { toast } from '@/components/common/toast'
import { useForgotPasswordMutation } from '@/hooks/react-query/auth'
import { validateEmail, validatePhone } from '@/lib/utils/validation'
import { hidePopup, showPopup } from '@/providers/popup-provider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { useTurnstile } from '@/hooks/useTurnstile'
import { OtpForm } from './otp-form'
import { ResetPassword } from './reset-password'

export const ForgotPasswordForm = () => {
  const [error, setError] = useState('')
  const t = useTranslations('login')
  const { TurnstileWidget, isReady, verify, reset } = useTurnstile({
    action: 'forgot_password',
    appearance: 'always',
  })

  const formSchema = z.object({
    address: z
      .string()
      .min(1, t('required_username'))
      .refine(
        (value) => {
          const isEmail = validateEmail(value)
          const phoneValidate = validatePhone(value)
          return isEmail || phoneValidate.valid
        },
        (value) => {
          if (value.includes('@')) {
            return {
              message: t('invalid_username'),
            }
          }

          if (typeof +value === 'number' && !isNaN(+value)) {
            return {
              message: t('invalid_phone'),
            }
          }

          return {
            message: t('invalid_username'),
          }
        }
      ),
  })

  const { mutateAsync: forgotPassword, isPending } = useForgotPasswordMutation()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      address: '',
    },
  })

  const handleShowResetPassword = async (forgotCode: string) => {
    showPopup(
      <ResetPassword
        forgotCode={forgotCode}
        onClose={() => hidePopup()}
        onSuccess={() => {
          toast.message({
            title: t('success'),
            message: t('reset_success'),
          })
        }}
      />
    )
  }

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      setError('')
      const turnstileRes = await verify()
      if (!turnstileRes.success) {
        toast.message({
          title: t('error'),
          message: 'Vui lòng xác thực robot',
        })
        reset()
        return
      }

      const isEmail = validateEmail(values.address)

      const forgotPasswordBody = {
        type: isEmail ? 'email' : 'phone',
        value: values.address,
      } as const

      const res = await forgotPassword(forgotPasswordBody)

      if (!res.state) {
        setError(res.message)
        return
      }

      // case phone
      if (isEmail) {
        // TODO: implement email
        toast.message({
          title: t('success'),
          message: t('otp_sent_success_email'),
          duration: 5000,
        })
        hidePopup()
      } else {
        toast.message({
          title: t('success'),
          message: t('otp_sent_success'),
        })
        setTimeout(() => {
          showPopup(
            <OtpForm
              phone={values.address}
              onSuccess={handleShowResetPassword}
              type='forgot_password'
              isAuth={true}
              onClose={() => hidePopup()}
            />
          )
        }, 100)
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setError(error.message)
      } else if (
        typeof error === 'object' &&
        error !== null &&
        'message' in error
      ) {
        setError((error as { message: string }).message)
      } else {
        setError(t('forgot_fail'))
      }
    } finally {
      reset()
    }
  }

  return (
    <>
      <h3 className='font-criteriacapitalize text-heading-5 mb-4 text-center font-medium tracking-normal text-neutral-900'>
        {t('forgot_password_title')}
      </h3>

      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
        <Input
          label={t('email_or_phone')}
          className='w-full'
          floating
          autoFocus
          error={form.formState.errors?.address}
          {...form.register('address')}
        />
        {error && <p className='mt-1 text-sm text-red-500'>{error}</p>}

        <div className='flex justify-center'>{TurnstileWidget}</div>

        <Button
          className='mt-6 w-full normal-case'
          variant='black'
          loading={isPending}
          disabled={!form.formState.isValid || !isReady || isPending}
        >
          {t('send_request')}
        </Button>
      </form>
    </>
  )
}
